import React from "react";
import { FOCUS_TONE, LOAD_LEVEL } from "@/lib/pastoral-scheduling";

/* Static tone style map — no dynamic Tailwind */
const TONE_STYLE = {
  [FOCUS_TONE.URGENT]: {
    bg:     "bg-orange-50 dark:bg-orange-950/20",
    title:  "text-orange-700 dark:text-orange-400",
    sub:    "text-orange-600/70 dark:text-orange-500/60",
    bullet: "bg-orange-300 dark:bg-orange-600",
  },
  [FOCUS_TONE.NURTURING]: {
    bg:     "bg-blue-50 dark:bg-blue-950/20",
    title:  "text-blue-700 dark:text-blue-400",
    sub:    "text-blue-600/70 dark:text-blue-500/60",
    bullet: "bg-blue-300 dark:bg-blue-600",
  },
  [FOCUS_TONE.BALANCED]: {
    bg:     "bg-violet-50 dark:bg-violet-950/20",
    title:  "text-violet-700 dark:text-violet-400",
    sub:    "text-violet-600/70 dark:text-violet-500/60",
    bullet: "bg-violet-300 dark:bg-violet-600",
  },
  [FOCUS_TONE.HEALTHY]: {
    bg:     "bg-emerald-50 dark:bg-emerald-950/20",
    title:  "text-emerald-700 dark:text-emerald-400",
    sub:    "text-emerald-600/70 dark:text-emerald-500/60",
    bullet: "bg-emerald-300 dark:bg-emerald-600",
  },
};

/* Load indicator dots — static */
const LOAD_DOT = {
  [LOAD_LEVEL.LIGHT]:      "bg-emerald-400",
  [LOAD_LEVEL.MODERATE]:   "bg-amber-400",
  [LOAD_LEVEL.HEAVY]:      "bg-orange-400",
  [LOAD_LEVEL.OVERLOADED]: "bg-red-500",
};

const LOAD_LABEL = {
  [LOAD_LEVEL.LIGHT]:      "Ligera",
  [LOAD_LEVEL.MODERATE]:   "Moderada",
  [LOAD_LEVEL.HEAVY]:      "Exigente",
  [LOAD_LEVEL.OVERLOADED]: "Al límite",
};

/**
 * WeeklyFocusCard — compact pastoral week synthesis.
 *
 * Renders:
 * 1. Load indicator (dot + level label)
 * 2. Weekly focus headline (tone-colored)
 * 3. Context line (why this focus)
 * 4. Top 2–3 pastoral action suggestions
 *
 * Returns null when the district is healthy + light load — the absence
 * of the card itself signals everything is going well.
 *
 * Input: { focus, load } from buildWeeklyFocus() + estimatePastoralLoad().
 * NOT a task manager — a pastoral week briefing.
 */
export default function WeeklyFocusCard({ focus, load }) {
  if (!focus || !load) return null;

  // No card when everything is truly healthy — silence = good signal
  if (focus.tone === FOCUS_TONE.HEALTHY && load.level === LOAD_LEVEL.LIGHT) return null;

  const style    = TONE_STYLE[focus.tone] ?? TONE_STYLE[FOCUS_TONE.BALANCED];
  const loadDot  = LOAD_DOT[load.level]   ?? LOAD_DOT[LOAD_LEVEL.MODERATE];
  const loadLbl  = LOAD_LABEL[load.level] ?? "Moderada";

  return (
    <div className={`rounded-[20px] px-4 py-3.5 ${style.bg}`}>

      {/* Load indicator */}
      <div className="flex items-center gap-1.5 mb-2">
        <div className={`h-1.5 w-1.5 rounded-full ${loadDot}`} />
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
          Carga pastoral · {loadLbl}
        </p>
      </div>

      {/* Headline */}
      <p className={`text-[13px] font-semibold leading-snug ${style.title}`}>
        {focus.headline}
      </p>

      {/* Context */}
      {focus.context && (
        <p className={`text-[11px] mt-1 leading-snug ${style.sub}`}>
          {focus.context}
        </p>
      )}

      {/* Suggestions */}
      {focus.suggestions && focus.suggestions.length > 0 && (
        <div className="mt-2.5 space-y-1">
          {focus.suggestions.map((s, i) => (
            <div key={i} className="flex items-start gap-2">
              <div className={`h-1.5 w-1.5 rounded-full shrink-0 mt-1 ${style.bullet}`} />
              <p className={`text-[11px] leading-snug ${style.sub}`}>{s}</p>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}
